/**
 * Окно чата с пациентом: фильтры по каналам (WhatsApp/Telegram), выбор канала отправки
 * Отображает историю сообщений, информацию о пациенте и привязках Telegram
 * Дата: 30.12.2025 - Обновлено для TODO-024: цветовые темы по каналу
 * Дата: 24.01.2026 - Добавлено логирование действий пользователя
 * Дата: 05.02.2026 - Добавлена кнопка прикрепления медиа файлов (TODO-055)
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import { useMessages } from '@/hooks/useMessages';
import { useFamilyByPhone } from '@/hooks/useFamilies';
import { logger } from '@/utils/logger';
import { MessageBubble } from './MessageBubble';
import { TrainingNotesPanel } from './TrainingNotesPanel';
import { TelegramLinkPanel } from '@/components/messages/TelegramLinkPanel';
import { MaxLinkPanel } from '@/components/messages/MaxLinkPanel';
import { Button, Spinner } from '@/components/ui';
import { Send, Phone, User, AtSign, UsersRound, MessageSquarePlus, CheckCheck, Paperclip, X, FileText, Brain, Sparkles, History, StickyNote, PanelRight } from 'lucide-react';
import { useTenantScope } from '@/hooks/useTenantScope';
import { trainingNotesApi } from '@/api/trainingNotes';
import { cn } from '@/utils/cn';
import { PatientBinding } from '@/api/types';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { WABATemplate } from '@/api/templates';
import { wabaApi } from '@/api/waba';
import { useWABATemplates } from '@/hooks/useWABATemplates';
import { useToast } from '@/hooks/useToast';
import { WABATemplatePicker } from './WABATemplatePicker';
import { WABATemplateForm } from './WABATemplateForm';
import { WindowStatusIndicator } from './WindowStatusIndicator';
import { PatientContextModal } from './PatientContextModal';
import { useRequestContact } from '@/hooks/useLinking'; // Дата: 24.01.2026 - TODO-033: Для запроса контакта
import { useTenantConfig } from '@/hooks/useTenantConfig'; // Дата: 10.02.2026 - TODO-021b: TG Client status для inline search
import { CopilotDrawer } from './copilot/CopilotDrawer';
import { ComposerAIBadge } from './copilot/ComposerAIBadge';
import { CopilotHistoryModal } from './copilot/CopilotHistoryModal';
import { EMPTY_AI_DRAFT, type AIDraftState } from './copilot/types';

// 24.10.2026 — фон переписки нейтральный. Канал отмечается в metadata сообщения, не заливкой.
// Не удалять по требованию владельца системы. CROSS-REF: MessageBubble.tsx, pages/Messages.tsx
const chatPatterns = {
  telegram: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Cdefs%3E%3Cstyle%3E.cls-1%7Bfill:%23b8d4f0;opacity:0.3;%7D%3C/style%3E%3C/defs%3E%3Ccircle class='cls-1' cx='20' cy='20' r='4'/%3E%3Ccircle class='cls-1' cx='80' cy='40' r='3'/%3E%3Ccircle class='cls-1' cx='140' cy='30' r='5'/%3E%3Ccircle class='cls-1' cx='180' cy='80' r='3'/%3E%3Ccircle class='cls-1' cx='60' cy='100' r='4'/%3E%3Ccircle class='cls-1' cx='120' cy='120' r='3'/%3E%3Ccircle class='cls-1' cx='40' cy='160' r='5'/%3E%3Ccircle class='cls-1' cx='160' cy='160' r='4'/%3E%3Ccircle class='cls-1' cx='100' cy='180' r='3'/%3E%3Cpath class='cls-1' d='M10 70 L15 65 L20 70 L15 75 Z'/%3E%3Cpath class='cls-1' d='M150 100 L155 95 L160 100 L155 105 Z'/%3E%3Cpath class='cls-1' d='M90 60 L95 55 L100 60 L95 65 Z'/%3E%3Cpath class='cls-1' d='M180 140 L185 135 L190 140 L185 145 Z'/%3E%3Crect class='cls-1' x='30' y='85' width='6' height='6' rx='1'/%3E%3Crect class='cls-1' x='170' y='25' width='6' height='6' rx='1'/%3E%3Crect class='cls-1' x='70' y='145' width='6' height='6' rx='1'/%3E%3Cpolygon class='cls-1' points='50 50 55 42 60 50 55 58'/%3E%3Cpolygon class='cls-1' points='130 70 135 62 140 70 135 78'/%3E%3Cpolygon class='cls-1' points='100 140 105 132 110 140 105 148'/%3E%3C/svg%3E")`,
  whatsapp: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Cdefs%3E%3Cstyle%3E.cls-1%7Bfill:%23c5d9c5;opacity:0.4;%7D%3C/style%3E%3C/defs%3E%3Ccircle class='cls-1' cx='25' cy='25' r='3'/%3E%3Ccircle class='cls-1' cx='85' cy='45' r='4'/%3E%3Ccircle class='cls-1' cx='145' cy='35' r='3'/%3E%3Ccircle class='cls-1' cx='175' cy='85' r='4'/%3E%3Ccircle class='cls-1' cx='55' cy='105' r='3'/%3E%3Ccircle class='cls-1' cx='115' cy='125' r='4'/%3E%3Ccircle class='cls-1' cx='35' cy='165' r='3'/%3E%3Ccircle class='cls-1' cx='165' cy='155' r='3'/%3E%3Ccircle class='cls-1' cx='95' cy='175' r='4'/%3E%3C/svg%3E")`,
  max: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Cdefs%3E%3Cstyle%3E.cls-1%7Bfill:%23d0c0e0;opacity:0.3;%7D%3C/style%3E%3C/defs%3E%3Ccircle class='cls-1' cx='20' cy='20' r='4'/%3E%3Ccircle class='cls-1' cx='80' cy='40' r='3'/%3E%3Ccircle class='cls-1' cx='140' cy='30' r='5'/%3E%3Ccircle class='cls-1' cx='180' cy='80' r='3'/%3E%3Ccircle class='cls-1' cx='60' cy='100' r='4'/%3E%3Ccircle class='cls-1' cx='120' cy='120' r='3'/%3E%3Ccircle class='cls-1' cx='40' cy='160' r='5'/%3E%3Ccircle class='cls-1' cx='160' cy='160' r='4'/%3E%3Cpath class='cls-1' d='M10 70 L15 65 L20 70 L15 75 Z'/%3E%3Cpath class='cls-1' d='M150 100 L155 95 L160 100 L155 105 Z'/%3E%3C/svg%3E")`,
  sms: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Cdefs%3E%3Cstyle%3E.cls-1%7Bfill:%23999;opacity:0.2;%7D%3C/style%3E%3C/defs%3E%3Crect class='cls-1' x='20' y='20' width='8' height='12' rx='2'/%3E%3Crect class='cls-1' x='80' y='40' width='8' height='12' rx='2'/%3E%3Crect class='cls-1' x='140' y='25' width='8' height='12' rx='2'/%3E%3Crect class='cls-1' x='175' y='80' width='8' height='12' rx='2'/%3E%3Crect class='cls-1' x='50' y='100' width='8' height='12' rx='2'/%3E%3Crect class='cls-1' x='110' y='120' width='8' height='12' rx='2'/%3E%3Crect class='cls-1' x='35' y='160' width='8' height='12' rx='2'/%3E%3Crect class='cls-1' x='165' y='150' width='8' height='12' rx='2'/%3E%3Crect class='cls-1' x='95' y='175' width='8' height='12' rx='2'/%3E%3C/svg%3E")`
};

// Inline style для паттерна
const getChatBackgroundStyle = (_channel: string): React.CSSProperties => {
  void chatPatterns;
  return { backgroundColor: 'var(--shell-bg)' };
};

const CHANNEL_MENU: Array<{ id: ActiveChannel; label: string; testId: string }> = [
  { id: 'all', label: 'Все', testId: 'chat-channel-tab-all' },
  { id: 'whatsapp_waba', label: 'WABA', testId: 'chat-channel-tab-waba' },
  { id: 'whatsapp_regular', label: 'Reg', testId: 'chat-channel-tab-regular' },
  { id: 'telegram_bot', label: 'TG Bot', testId: 'chat-channel-tab-telegram-bot' },
  { id: 'telegram_client', label: 'TG Client', testId: 'chat-channel-tab-telegram-client' },
  { id: 'max', label: 'MAX', testId: 'chat-channel-tab-max' },
  { id: 'max_client', label: 'Max Cli', testId: 'chat-channel-tab-max-client' },
  { id: 'sms', label: 'SMS', testId: 'chat-channel-tab-sms' },
];

interface ChatWindowProps {
  phone: string;
  tenantId?: string; // Дата: 28.12.2025 - Добавлен для SuperAdmin
  patientName?: string; // Дата: 30.12.2025 - Имя пациента из списка бесед
}

// Дата: 30.12.2025 - Обновлено: разделение WhatsApp на WABA и Reg, Telegram на Bot и Client
type ActiveChannel = 'all' | 'whatsapp_waba' | 'whatsapp_regular' | 'telegram_bot' | 'telegram_client' | 'sms' | 'sms_tel' | 'max' | 'max_client';

const isMessageInChannel = (
  message: {
    messenger?: string;
    messenger_type?: string;
  },
  channel: ActiveChannel
): boolean => {
  // Registry note:
  // канальный registry на фронте уже есть, но messages feed сейчас приходит не строго
  // в canonical виде. Здесь намеренно держим compatibility-матчинг по messenger +
  // messenger_type, чтобы старые telegram/whatsapp/wazzup записи не выпадали из UI.
  if (channel === 'all') return true;

  const messenger = message.messenger || '';
  const messengerType = message.messenger_type || '';

  switch (channel) {
    case 'whatsapp_waba':
      return (
        messengerType === 'waba' ||
        messenger === 'whatsapp_waba' ||
        messenger === 'wazzup_waba' ||
        ((messenger === 'whatsapp' || messenger === 'wazzup') && messengerType === 'waba')
      );
    case 'whatsapp_regular':
      return (
        messengerType === 'regular' ||
        messenger === 'whatsapp_regular' ||
        messenger === 'wazzup_regular' ||
        ((messenger === 'whatsapp' || messenger === 'wazzup') &&
          messengerType !== 'waba' &&
          !messenger.includes('waba'))
      );
    case 'telegram_bot':
      return messenger === 'telegram' || messenger === 'telegram_bot';
    case 'telegram_client':
      return messenger === 'telegram_client';
    case 'max':
      return messenger === 'max';
    case 'max_client':
      return messenger === 'max_client';
    case 'sms':
      return messenger === 'sms' || messenger === 'sms_tel' || messenger === 'sms-tel';
    case 'sms_tel':
      return messenger === 'sms_tel' || messenger === 'sms-tel' || messenger === 'smstel';
    default:
      return false;
  }
};

const getReadChannelApiParam = (channel: ActiveChannel): string | undefined => {
  // Registry note:
  // это API boundary glue, а не новый реестр: backend mark-read endpoint всё ещё ждёт
  // исторические query values вроде regular / waba / telegram_bot.
  switch (channel) {
    case 'whatsapp_waba':
      return 'waba';
    case 'whatsapp_regular':
      return 'regular';
    case 'telegram_bot':
      return 'telegram_bot';
    case 'telegram_client':
      return 'telegram_client';
    case 'max':
      return 'max';
    case 'max_client':
      return 'max_client';
    case 'sms':
      return 'sms';
    default:
      return undefined;
  }
};

export function ChatWindow({ phone, tenantId, patientName }: ChatWindowProps) {
  const [activeChannel, setActiveChannel] = useState<ActiveChannel>('all');
  const [channelMenuOpen, setChannelMenuOpen] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [enterSends, setEnterSends] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem('mt.composer.enterSends') !== '0';
  });
  const draftsRef = useRef<Record<string, string>>({});
  const draftKey = `${phone}::${activeChannel}`;
  useEffect(() => {
    setMessage(draftsRef.current[draftKey] || '');
  }, [draftKey]);
  const [isContextModalOpen, setIsContextModalOpen] = useState(false);
  const [isCopilotOpen, setIsCopilotOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [aiDraft, setAiDraft] = useState<AIDraftState>(EMPTY_AI_DRAFT);
  // Дата: 05.02.2026 - TODO-055: состояние прикреплённого файла
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [attachedPreview, setAttachedPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Дата: 15.01.2026 - TODO-027a: состояния WABA template picker/form
  const [isWabaPickerOpen, setIsWabaPickerOpen] = useState(false);
  const [wabaQuery, setWabaQuery] = useState('');
  const [selectedWabaTemplate, setSelectedWabaTemplate] = useState<WABATemplate | null>(null);
  const [isWabaFormOpen, setIsWabaFormOpen] = useState(false);
  // Дата: 08.01.2026 - Удалён selectedSendChannel: канал отправки определяется activeChannel
  // 12.02.2026 — Модалка выбора канала для request-contact (TG Bot / TG Client)
  const [isContactChannelModalOpen, setIsContactChannelModalOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const shouldStickToBottomRef = useRef(true);
  const pendingPrependRestoreRef = useRef<{ prevHeight: number; prevTop: number } | null>(null);
  const previousPhoneRef = useRef<string | null>(null);
  const previousMessagesCountRef = useRef(0);
  const queryClient = useQueryClient();
  const toast = useToast();
  const { isSuperAdmin } = useTenantScope();
  const [notesOpen, setNotesOpen] = useState(false);
  const [selectedNoteMessageId, setSelectedNoteMessageId] = useState<string | null>(null);
  
  // Дата: 24.01.2026 - TODO-033: Hook для запроса контакта
  const requestContactMutation = useRequestContact();
  
  // Дата: 28.12.2025 - Добавлен tenantId для SuperAdmin
  // Загружаем все сообщения для подсчета по каналам
  // Дата: 26.01.2026 - Добавлено markAsRead для раздельной отметки прочитанными
  const { 
    messages: allMessages, 
    totalMessages,
    hasMoreMessages,
    fetchMoreMessages,
    isFetchingMoreMessages,
    patient, 
    bindings,
    isLoading, 
    sendMessage,
    isSending,
    markAsRead,
    isMarkingAsRead,
  } = useMessages(phone, undefined, tenantId); // Загружаем все сообщения без фильтра

  // 25.08.2026 — НЕ УДАЛЯТЬ по требованию владельца: служебные пометки только superadmin, пациент не видит.
  // Separate request after chat open. Do not enrich WS / message DTO.
  const messageIdsKey = allMessages.map((m) => m.id).join(',');
  const notesQuery = useQuery({
    queryKey: ['training-notes', tenantId, phone, messageIdsKey],
    queryFn: () => trainingNotesApi.listForChat(tenantId || '', phone, allMessages.map((m) => m.id)),
    enabled: Boolean(isSuperAdmin && tenantId && phone),
  });
  const notesByMessageId = useMemo(() => {
    const ids = new Set<string>();
    for (const note of notesQuery.data || []) {
      if (note.anchor_kind === 'message') ids.add(note.anchor_id);
    }
    return ids;
  }, [notesQuery.data]);
  
  // Дата: 09.01.2026 - TODO-029: Получаем информацию о семье для badge
  const { family } = useFamilyByPhone(phone, tenantId);
  
  // Дата: 10.02.2026 - TODO-021b: TG Client статус для inline поиска в TelegramLinkPanel
  const { messengerStatus } = useTenantConfig(tenantId);
  // macSuggest теперь живёт внутри CopilotDrawer
  
  // Дата: 08.01.2026 - Удалён useEffect для selectedSendChannel
  // Канал отправки теперь определяется activeChannel (выбранный справа)
  
  // Дата: 28.12.2025 - Добавлено для TODO-018: подсчет сообщений по каналам
  // Дата: 30.12.2025 - Обновлено: разделение WhatsApp на WABA/Reg, Telegram на Bot/Client
  // Дата: 28.01.2026 - FIX: Поддержка старых данных с пустым messenger_type + учёт wazzup_regular/wazzup_waba в messenger
  const channelCounts = {
    all: allMessages.length,
    // WABA: messenger_type='waba' ИЛИ messenger содержит 'waba'
    whatsapp_waba: allMessages.filter((m) => isMessageInChannel(m, 'whatsapp_waba')).length,
    // Regular: messenger_type='regular' ИЛИ (messenger содержит 'whatsapp' или 'wazzup' но НЕ waba И messenger_type не задан/пустой)
    whatsapp_regular: allMessages.filter((m) => isMessageInChannel(m, 'whatsapp_regular')).length,
    telegram_bot: allMessages.filter((m) => isMessageInChannel(m, 'telegram_bot')).length,
    telegram_client: allMessages.filter((m) => isMessageInChannel(m, 'telegram_client')).length,
    sms: allMessages.filter((m) => isMessageInChannel(m, 'sms')).length,
    max: allMessages.filter((m) => isMessageInChannel(m, 'max')).length,
    max_client: allMessages.filter((m) => isMessageInChannel(m, 'max_client')).length,
  };
  
  // Дата: 26.01.2026 - Подсчёт непрочитанных входящих по каналам
  // Дата: 29.01.2026 - Добавлен SMS (TODO-031)
  const incomingMessages = allMessages.filter(m => m.direction === 'incoming');
  const unreadCounts = {
    all: incomingMessages.filter(m => !m.is_read).length,
    waba: incomingMessages.filter((m) => !m.is_read && isMessageInChannel(m, 'whatsapp_waba')).length,
    regular: incomingMessages.filter((m) => !m.is_read && isMessageInChannel(m, 'whatsapp_regular')).length,
    telegram_bot: incomingMessages.filter((m) => !m.is_read && isMessageInChannel(m, 'telegram_bot')).length,
    telegram_client: incomingMessages.filter((m) => !m.is_read && isMessageInChannel(m, 'telegram_client')).length,
    max: incomingMessages.filter((m) => !m.is_read && isMessageInChannel(m, 'max')).length,
    max_client: incomingMessages.filter((m) => !m.is_read && isMessageInChannel(m, 'max_client')).length,
    sms: incomingMessages.filter((m) => !m.is_read && isMessageInChannel(m, 'sms')).length,
  };
  
  // Дата: 26.01.2026 - Функция для отметки прочитанными по каналу
  const handleMarkAsRead = async (channel?: string) => {
    if (!phone) return;
    try {
      await markAsRead({ phone, channel });
      logger.info('mark_as_read_success', { phone, channel: channel || 'all' });
      toast.showSuccess(channel ? `${channel} отмечен прочитанным` : 'Все отмечены прочитанными');
    } catch (error) {
      logger.error('mark_as_read_failed', { phone, channel, error });
      toast.showError('Не удалось отметить прочитанными');
    }
  };
  
  // 04.05.2026 — генерация AI-подсказки через MAC
  const handleApplyVariant = (text: string, suggestionId: string, eventId: string, mode: string, _variantIdx: number) => {
    setMessage(text);
    setAiDraft({ kind: 'ai_clean', suggestionId, eventId, mode, originalText: text });
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      textareaRef.current.focus();
    }
  };

  // Автоскролл к последнему сообщению (изолированный, без влияния на левую панель)
  // Дата: 30.12.2025 - Исправлено: используем scrollTop вместо scrollIntoView для изоляции скролла
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    if (previousPhoneRef.current !== phone) {
      container.scrollTop = container.scrollHeight;
      previousPhoneRef.current = phone;
      previousMessagesCountRef.current = allMessages.length;
      pendingPrependRestoreRef.current = null;
      return;
    }

    if (pendingPrependRestoreRef.current) {
      const { prevHeight, prevTop } = pendingPrependRestoreRef.current;
      const delta = container.scrollHeight - prevHeight;
      container.scrollTop = prevTop + delta;
      pendingPrependRestoreRef.current = null;
      previousMessagesCountRef.current = allMessages.length;
      return;
    }

    const hasNewMessages = allMessages.length > previousMessagesCountRef.current;
    if (hasNewMessages && shouldStickToBottomRef.current) {
      container.scrollTop = container.scrollHeight;
    }
    previousMessagesCountRef.current = allMessages.length;
  }, [allMessages, phone]);
  
  // Фильтрация по каналу
  // Дата: 30.12.2025 - Обновлено: разделение WhatsApp на WABA/Reg, Telegram на Bot/Client
  // Дата: 28.01.2026 - FIX: Поддержка старых данных с пустым messenger_type + учёт wazzup_regular/wazzup_waba
  const filteredMessages = activeChannel === 'all' 
    ? allMessages 
    : allMessages.filter((m) => isMessageInChannel(m, activeChannel));

  const handleMessagesScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    shouldStickToBottomRef.current = distanceToBottom < 120;

    if (container.scrollTop < 120 && hasMoreMessages && !isFetchingMoreMessages) {
      pendingPrependRestoreRef.current = {
        prevHeight: container.scrollHeight,
        prevTop: container.scrollTop,
      };
      void fetchMoreMessages();
    }
  };
  
  // Определение основного канала для цветовой темы
  // Дата: 30.12.2025 - Обновлено: учет whatsapp_waba/regular, telegram_bot/client
  const primaryChannel = useMemo(() => {
    if (activeChannel !== 'all') {
      // Для WABA и Regular используем whatsapp для цветовой темы
      if (activeChannel === 'whatsapp_waba' || activeChannel === 'whatsapp_regular') {
        return 'whatsapp';
      }
      // Для TG Bot и TG Client используем telegram для цветовой темы
      if (activeChannel === 'telegram_bot' || activeChannel === 'telegram_client') {
        return 'telegram';
      }
      return activeChannel;
    }
    // Определяем по последнему сообщению или самому частому каналу
    if (allMessages.length === 0) return 'whatsapp';
    const lastMessage = allMessages[allMessages.length - 1];
    return lastMessage.messenger || 'whatsapp';
  }, [activeChannel, allMessages]);
  
  const chatBackgroundStyle = getChatBackgroundStyle(primaryChannel);

  // Дата: 15.01.2026 - TODO-027a: базовые признаки для WABA логики
  const isPhoneNumber = /^7\d{10}$/.test(phone);
  const isWabaActive = activeChannel === 'whatsapp_waba';

  // TODO-027a: window-status (24h окно) для WABA
  const wabaWindowQuery = useQuery({
    queryKey: ['waba-window-status', tenantId, phone],
    queryFn: () => wabaApi.getWindowStatus(tenantId, phone),
    enabled: isWabaActive && !!tenantId && isPhoneNumber,
    refetchInterval: 60_000,
  });
  const isWabaWindowOpen = !!wabaWindowQuery.data?.window_open;
  // 15.09.2026. Не удалять по требованию владельца системы.
  // Fail-closed: пока статус не подтверждён, свободный текст нельзя.
  const isWabaWindowClosed = isWabaActive && isPhoneNumber && !isWabaWindowOpen;

  // TODO-027a: templates list
  const wabaTemplatesQuery = useWABATemplates(tenantId);
  const wabaTemplates = wabaTemplatesQuery.data?.templates || [];

  const sendTemplateMutation = useMutation({
    mutationFn: async (t: WABATemplate) => {
      return wabaApi.sendTemplate({
        tenant_id: tenantId,
        phone,
        template_guid: t.template_guid,
        template_name: t.name,
        variables: {},
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast.showSuccess('Шаблон отправлен');
    },
    onError: (e: any) => {
      toast.showError(e?.message || 'Не удалось отправить шаблон');
    },
  });

  // TODO-027a: обработка выбора WABA template
  const handlePickWabaTemplate = (t: WABATemplate) => {
    setSelectedWabaTemplate(t);
    setIsWabaPickerOpen(false);
    setWabaQuery('');
    setMessage('');
    if (t.variables && t.variables.length > 0) {
      setIsWabaFormOpen(true);
      return;
    }
    sendTemplateMutation.mutate(t);
  };
  
  const handleSend = async () => {
    if (!message.trim()) return;
    // TODO-027a: если введён `/...` — это режим выбора шаблона, обычную отправку не делаем
    if (isWabaActive && message.startsWith('/')) {
      setIsWabaPickerOpen(true);
      setWabaQuery(message.slice(1));
      return;
    }
    // 24.10.2026 — канал отправки только явный. «Все» не подменяет канал последним сообщением.
    if (activeChannel === 'all') {
      toast.showWarning('Выберите канал отправки. Канал сам не переключается.');
      setChannelMenuOpen(true);
      return;
    }
    const channelToSend = activeChannel;
    // TODO-027a: WABA — если окно закрыто, запрещаем обычную отправку
    if (channelToSend === 'whatsapp_waba' && isWabaWindowClosed && !message.startsWith('/')) {
      toast.showWarning('Окно WABA закрыто — используйте / для выбора шаблона');
      logger.warning('waba_window_closed_block', { channel: channelToSend });
      return;
    }
    
    // Дата: 24.01.2026 - Логируем UI действие отправки
    logger.info('chat_message_send_click', {
      channel: channelToSend,
      active_channel: activeChannel,
      message_length: message.length,
    });
    
    try {
      await sendMessage({
        phone,
        channel: channelToSend,
        content: message,
        content_type: 'text',
        ai_source: aiDraft.kind !== 'none' ? aiDraft.kind : null,
        ai_suggestion_id: aiDraft.kind !== 'none' ? aiDraft.suggestionId : null,
        ai_event_id: aiDraft.kind !== 'none' ? aiDraft.eventId : null,
      });
      setMessage('');
      draftsRef.current[`${phone}::${activeChannel}`] = '';
      setAiDraft(EMPTY_AI_DRAFT);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      // Принудительно скроллим вниз после отправки
      shouldStickToBottomRef.current = true;
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
      
      // 29.09.2026 — не удалять. Исходящее снимает непрочитанные по всем каналам беседы.
      markAsRead({ phone }).catch(() => {});
      
    } catch (error) {
      // Ошибка уже залогирована в messagesApi.sendMessage
      // Показываем toast пользователю
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      toast.showError(axiosError.response?.data?.error || 'Ошибка отправки сообщения');
    }
  };
  
  // Проверка привязок
  const telegramBinding = bindings?.find((b: PatientBinding) => b.messenger === 'telegram' && b.is_verified);
  const telegramClientBinding = bindings?.find((b: PatientBinding) => b.messenger === 'telegram_client' && b.is_verified);
  const maxBinding = bindings?.find((b: PatientBinding) => b.messenger === 'max' && b.is_verified);
  
  // Дата: 29.12.2025 - Определяем, является ли phone телефоном или ID мессенджера
  // Дата: 10.01.2026 - Исправлено: tg: префикс означает Telegram ID независимо от канала
  // Дата: 24.01.2026 - TODO-033: Проверка messenger_id формата
  const isMessengerID = phone.startsWith('tg:') || phone.startsWith('max:');
  const isTelegramId = phone.startsWith('tg:') || (!isPhoneNumber && primaryChannel.includes('telegram'));
  const isMaxId = phone.startsWith('max:') || (!isPhoneNumber && !phone.startsWith('tg:') && (primaryChannel === 'max' || /^\d+$/.test(phone) && activeChannel === 'max'));
  
  // Дата: 24.01.2026 - TODO-033: Определение канала и messenger_id для запроса контакта
  const getContactRequestChannel = (): 'telegram_bot' | 'telegram_client' | 'max' | null => {
    if (phone.startsWith('tg:')) {
      // Определяем по последнему сообщению или активному каналу
      const lastMsg = allMessages[allMessages.length - 1];
      if (lastMsg?.messenger === 'telegram_client') return 'telegram_client';
      return 'telegram_bot'; // По умолчанию telegram_bot
    }
    if (phone.startsWith('max:') || primaryChannel === 'max') {
      return 'max';
    }
    return null;
  };
  
  const getMessengerIDFromPhone = (): string | null => {
    if (phone.startsWith('tg:')) {
      return phone.substring(3); // Убираем префикс tg:
    }
    if (phone.startsWith('max:')) {
      return phone.substring(4); // Убираем префикс max:
    }
    // Если это числовой ID без префикса
    if (!isPhoneNumber && /^\d+$/.test(phone)) {
      if (primaryChannel.includes('telegram')) {
        return phone;
      }
      if (primaryChannel === 'max') {
        return phone;
      }
    }
    return null;
  };
  
  // 12.02.2026 — Открывает модалку выбора канала при TG, или сразу отправляет для MAX
  const handleRequestContact = async () => {
    const messengerID = getMessengerIDFromPhone();
    if (!messengerID) {
      toast.showError('Не удалось определить messenger ID');
      return;
    }
    
    // Для MAX — отправляем сразу, без модалки
    if (phone.startsWith('max:') || primaryChannel === 'max') {
      await sendRequestContact(messengerID, 'max');
      return;
    }
    
    // Для TG — открываем модалку выбора канала (Bot / Client)
    setIsContactChannelModalOpen(true);
  };
  
  // 12.02.2026 — Фактическая отправка запроса контакта
  const sendRequestContact = async (messengerID: string, channel: 'telegram_bot' | 'telegram_client' | 'max') => {
    const channelNames: Record<string, string> = {
      telegram_bot: 'TG Bot',
      telegram_client: 'TG Client',
      max: 'MAX',
    };
    try {
      await requestContactMutation.mutateAsync({
        messenger_id: messengerID,
        channel: channel,
        tenant_id: tenantId,
      });
      toast.showSuccess(`Запрос контакта отправлен через ${channelNames[channel] || channel}`);
      setIsContactChannelModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['patient'] });
      queryClient.invalidateQueries({ queryKey: ['bindings'] });
    } catch (error: any) {
      toast.showError(error?.message || 'Не удалось отправить запрос контакта');
    }
  };
  
  // Доступные каналы для отправки с учётом привязок пациента
  // Дата: 29.12.2025 - Исправлено: если только TG ID - не показываем WhatsApp
  // Дата: 30.12.2025 - Добавлен telegram_client
  // Дата: 10.01.2026 - Исправлено: предлагать только привязанные каналы (кроме текущего)
  const availableChannels = useMemo(() => {
    const channels: string[] = [];
    
    // WhatsApp доступен ТОЛЬКО если есть номер телефона
    if (isPhoneNumber) {
      channels.push('whatsapp_waba');
      channels.push('whatsapp_regular');
    }
    
    // Telegram Bot доступен если:
    // 1. Пациент привязан через Telegram Bot
    // 2. ИЛИ у нас есть TG ID (переписка с "неизвестными")
    if (telegramBinding || isTelegramId) {
      channels.push('telegram_bot');
    }
    
    // Telegram Client доступен если:
    // 1. Есть tg:ID (можем отправить через MTProto)
    // 2. ИЛИ есть привязка telegram_client
    // 3. ИЛИ есть номер телефона (Client ищет по телефону)
    if (isTelegramId || telegramClientBinding || isPhoneNumber) {
      channels.push('telegram_client');
    }
    
    // MAX доступен если:
    // 1. Пациент привязан через MAX
    // 2. ИЛИ у нас есть MAX ID (переписка)
    if (maxBinding || isMaxId) {
      channels.push('max');
    }
    
    // SMS доступен ТОЛЬКО если есть номер телефона
    if (isPhoneNumber) {
      channels.push('sms');
    }
    
    return channels;
  }, [telegramBinding, telegramClientBinding, maxBinding, isPhoneNumber, isTelegramId, isMaxId]);
  
  // Дата: 09.01.2026 - Удален useEffect для setSelectedSendChannel (канал определяется activeChannel)
  // Дата: 09.01.2026 - Удален unboundChannels (не используется)
  
  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  
  return (
    <div className="flex-1 flex min-h-0">
    <div className="flex-1 flex flex-col min-h-0 min-w-0">
      {/* Header */}
      <div className="relative z-20 flex min-h-[64px] items-center justify-between border-b border-shell-border bg-shell-surface px-4 py-3">
        <div className="flex items-center gap-3">
          {/* Avatar with gradient */}
          <div className={cn(
            'w-11 h-11 rounded-2xl flex items-center justify-center shadow-md transition-transform hover:scale-105',
            'bg-gradient-to-br',
            primaryChannel.includes('whatsapp') ? 'from-emerald-400 to-emerald-600' :
            primaryChannel.includes('telegram') ? 'from-blue-400 to-blue-600' :
            primaryChannel === 'max' ? 'from-purple-400 to-purple-600' :
            primaryChannel === 'max_client' ? 'from-fuchsia-400 to-fuchsia-600' :
            primaryChannel === 'sms' ? 'from-amber-400 to-amber-600' :
            'from-primary-400 to-primary-600'
          )}>
            <User className="w-5 h-5 text-white drop-shadow-sm" />
          </div>
          
          <div>
            {/* Name + Family badge */}
            <div className="flex items-center gap-2">
              <h2 className="font-bold text-shell-strong text-base">
                {patientName || patient?.full_name || 'Неизвестный пациент'}
              </h2>
              {family && (
                <Link to={`/families/${family.id}`}>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold 
                                   bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700
                                   flex items-center gap-1 hover:shadow-sm transition-shadow cursor-pointer">
                    <UsersRound className="w-3 h-3" />
                    <span>{family.name}</span>
                    {family.members && family.members.length > 1 && (
                      <span className="opacity-70">({family.members.length})</span>
                    )}
                  </span>
                </Link>
              )}
            </div>
            
            {/* Contact info row */}
            <div className="flex items-center gap-2 text-xs text-shell-muted mt-0.5">
              {isMessengerID ? (
                <>
                  {phone.startsWith('tg:') ? (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50">
                      <span className="text-blue-600 font-semibold">TG</span>
                      <span className="text-shell-text">{phone.substring(3)}</span>
                    </span>
                  ) : phone.startsWith('max:') ? (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50">
                      <span className="text-purple-600 font-semibold">MAX</span>
                      <span className="text-shell-text">{phone.substring(4)}</span>
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-shell-elevated">
                      <span className="text-shell-text font-semibold">ID</span>
                      <span>{phone}</span>
                    </span>
                  )}
                  <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold">
                    📱 Телефон неизвестен
                  </span>
                </>
              ) : (
                <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-shell-elevated">
                  <Phone className="w-3 h-3 text-shell-muted" />
                  <span className="font-medium text-shell-text">{phone}</span>
                </span>
              )}
              {telegramBinding && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50">
                  <AtSign className="w-3 h-3 text-blue-500" />
                  <span className="text-blue-600 font-medium">@{telegramBinding.username || telegramBinding.messenger_id}</span>
                </span>
              )}
              {maxBinding && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50">
                  <span className="text-purple-600 font-medium">MAX: {maxBinding.messenger_id}</span>
                </span>
              )}
            </div>
            {/* Family account info - subtle */}
            {family && (
              <div className="flex items-center gap-1.5 text-[11px] mt-1.5 text-shell-muted">
                <UsersRound className="w-3 h-3 text-indigo-400" />
                {family.primary_contact_id && patient?.id === family.primary_contact_id ? (
                  <span>
                    <span className="font-medium text-indigo-600">Глава семьи:</span>{' '}
                    <Link to={`/families/${family.id}`} className="text-indigo-500 hover:underline">{family.name}</Link>
                  </span>
                ) : (
                  <span>
                    <span className="text-shell-muted">В семье:</span>{' '}
                    <Link to={`/families/${family.id}`} className="text-indigo-500 hover:underline">{family.name}</Link>
                  </span>
                )}
              </div>
            )}
            
            {/* Request contact button - 30.01.2026 REDESIGN */}
            {isMessengerID && getContactRequestChannel() && (
              <div className="mt-2">
                <button
                  onClick={handleRequestContact}
                  disabled={requestContactMutation.isPending}
                  className={cn(
                    'px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200',
                    'flex items-center gap-2',
                    'bg-gradient-to-r from-amber-400 to-amber-500 text-white shadow-md shadow-amber-200',
                    'hover:shadow-lg hover:scale-105',
                    requestContactMutation.isPending && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <MessageSquarePlus className="w-4 h-4" />
                  {requestContactMutation.isPending ? (
                    <>
                      <Spinner size="sm" />
                      <span>Отправка...</span>
                    </>
                  ) : (
                    <span>
                      Запросить контакт {getContactRequestChannel() === 'max' ? 'в MAX' : 'в Telegram'}
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
        
        <div className="relative">
          <button
            type="button"
            className="shell-button-secondary h-9"
            onClick={() => setChannelMenuOpen((open) => !open)}
          >
            {CHANNEL_MENU.find((item) => item.id === activeChannel)?.label || 'Канал'}
          </button>
          {channelMenuOpen && (
        <div
          className="absolute right-0 top-10 z-30 grid max-h-80 w-52 gap-1 overflow-y-auto rounded-xl border border-shell-border bg-shell-surface p-2"
          onClick={(event) => {
            if ((event.target as HTMLElement).closest('button')) setChannelMenuOpen(false);
          }}
        >
          {/* Tab: Все */}
          <button
            onClick={() => setActiveChannel('all')}
            className={cn(
              'px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200',
              'flex items-center gap-1.5',
              activeChannel === 'all' 
                ? 'bg-shell-strong text-shell-bg shadow-md' 
                : 'text-shell-muted hover:bg-shell-hover hover:text-shell-text'
            )}
            data-testid="chat-channel-tab-all"
          >
            <span>Все</span>
            <span className={cn(
              'px-1.5 py-0.5 rounded-md text-[10px] font-bold',
              activeChannel === 'all' ? 'bg-shell-bg/20' : 'bg-shell-elevated'
            )}>
              {channelCounts.all}
            </span>
          </button>
          
          {/* Tab: WABA */}
          <button
            onClick={() => setActiveChannel('whatsapp_waba')}
            className={cn(
              'px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200',
              'flex items-center gap-1.5',
              activeChannel === 'whatsapp_waba' 
                ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-md shadow-emerald-200'
                : 'text-shell-muted hover:bg-emerald-50 hover:text-emerald-700'
            )}
            data-testid="chat-channel-tab-waba"
          >
            <span>WABA</span>
            <span className={cn(
              'px-1.5 py-0.5 rounded-md text-[10px] font-bold',
              activeChannel === 'whatsapp_waba' ? 'bg-white/20' : 'bg-emerald-100 text-emerald-700'
            )}>
              {channelCounts.whatsapp_waba || '—'}
            </span>
          </button>
          
          {/* Tab: Reg */}
          <button
            onClick={() => setActiveChannel('whatsapp_regular')}
            className={cn(
              'px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200',
              'flex items-center gap-1.5',
              activeChannel === 'whatsapp_regular' 
                ? 'bg-gradient-to-r from-green-400 to-green-500 text-white shadow-md shadow-green-200'
                : 'text-shell-muted hover:bg-green-50 hover:text-green-700'
            )}
            data-testid="chat-channel-tab-regular"
          >
            <span>Reg</span>
            <span className={cn(
              'px-1.5 py-0.5 rounded-md text-[10px] font-bold',
              activeChannel === 'whatsapp_regular' ? 'bg-white/20' : 'bg-green-100 text-green-700'
            )}>
              {channelCounts.whatsapp_regular || '—'}
            </span>
          </button>
          
          {/* Tab: TG Bot */}
          <button
            onClick={() => setActiveChannel('telegram_bot')}
            className={cn(
              'px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200',
              'flex items-center gap-1.5',
              activeChannel === 'telegram_bot' 
                ? 'bg-gradient-to-r from-blue-400 to-blue-500 text-white shadow-md shadow-blue-200'
                : 'text-shell-muted hover:bg-blue-50 hover:text-blue-700'
            )}
            data-testid="chat-channel-tab-telegram-bot"
          >
            <span>TG Bot</span>
            <span className={cn(
              'px-1.5 py-0.5 rounded-md text-[10px] font-bold',
              activeChannel === 'telegram_bot' ? 'bg-white/20' : 'bg-blue-100 text-blue-700'
            )}>
              {channelCounts.telegram_bot || '—'}
            </span>
          </button>
          
          {/* Tab: TG Client */}
          <button
            onClick={() => setActiveChannel('telegram_client')}
            className={cn(
              'px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200',
              'flex items-center gap-1.5',
              activeChannel === 'telegram_client' 
                ? 'bg-gradient-to-r from-sky-400 to-sky-500 text-white shadow-md shadow-sky-200'
                : 'text-shell-muted hover:bg-sky-50 hover:text-sky-700'
            )}
            data-testid="chat-channel-tab-telegram-client"
          >
            <span>TG Cli</span>
            <span className={cn(
              'px-1.5 py-0.5 rounded-md text-[10px] font-bold',
              activeChannel === 'telegram_client' ? 'bg-white/20' : 'bg-sky-100 text-sky-700'
            )}>
              {channelCounts.telegram_client || '—'}
            </span>
          </button>
          
          {/* Tab: MAX */}
          <button
            onClick={() => setActiveChannel('max')}
            className={cn(
              'px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200',
              'flex items-center gap-1.5',
              activeChannel === 'max' 
                ? 'bg-gradient-to-r from-purple-400 to-purple-500 text-white shadow-md shadow-purple-200'
                : 'text-shell-muted hover:bg-purple-50 hover:text-purple-700'
            )}
            data-testid="chat-channel-tab-max"
          >
            <span>MAX</span>
            <span className={cn(
              'px-1.5 py-0.5 rounded-md text-[10px] font-bold',
              activeChannel === 'max' ? 'bg-white/20' : 'bg-purple-100 text-purple-700'
            )}>
              {channelCounts.max || '—'}
            </span>
          </button>

          {/* Tab: Max Cli */}
          <button
            onClick={() => setActiveChannel('max_client')}
            className={cn(
              'px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200',
              'flex items-center gap-1.5',
              activeChannel === 'max_client'
                ? 'bg-gradient-to-r from-fuchsia-400 to-fuchsia-500 text-white shadow-md shadow-fuchsia-200'
                : 'text-shell-muted hover:bg-fuchsia-50 hover:text-fuchsia-700'
            )}
            data-testid="chat-channel-tab-max-client"
          >
            <span>Max Cli</span>
            <span className={cn(
              'px-1.5 py-0.5 rounded-md text-[10px] font-bold',
              activeChannel === 'max_client' ? 'bg-white/20' : 'bg-fuchsia-100 text-fuchsia-700'
            )}>
              {channelCounts.max_client || '—'}
            </span>
          </button>
          
          {/* Tab: SMS - 30.01.2026 */}
          <button
            onClick={() => setActiveChannel('sms')}
            className={cn(
              'px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200',
              'flex items-center gap-1.5',
              activeChannel === 'sms' 
                ? 'bg-gradient-to-r from-amber-400 to-amber-500 text-white shadow-md shadow-amber-200'
                : 'text-shell-muted hover:bg-amber-50 hover:text-amber-700'
            )}
            data-testid="chat-channel-tab-sms"
          >
            <span>SMS</span>
            <span className={cn(
              'px-1.5 py-0.5 rounded-md text-[10px] font-bold',
              activeChannel === 'sms' ? 'bg-white/20' : 'bg-amber-100 text-amber-700'
            )}>
              {channelCounts.sms || '—'}
            </span>
          </button>
          </div>
          )}
        </div>
          
          {/* Кнопка "Отметить прочитанными" - 30.01.2026 REDESIGN */}
          {(() => {
            const getUnreadForChannel = () => {
              switch (activeChannel) {
                case 'whatsapp_waba': return unreadCounts.waba;
                case 'whatsapp_regular': return unreadCounts.regular;
                case 'telegram_bot': return unreadCounts.telegram_bot;
                case 'telegram_client': return unreadCounts.telegram_client;
                case 'max': return unreadCounts.max;
                case 'max_client': return unreadCounts.max_client;
                case 'sms': return unreadCounts.sms;
                default: return unreadCounts.all;
              }
            };
            const channelUnread = getUnreadForChannel();
            
            if (channelUnread > 0) {
              return (
                <button
                  onClick={() => handleMarkAsRead(getReadChannelApiParam(activeChannel))}
                  disabled={isMarkingAsRead}
                  className={cn(
                    'ml-3 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200',
                    'flex items-center gap-1.5',
                    'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md shadow-blue-200',
                    'hover:shadow-lg hover:scale-105',
                    isMarkingAsRead && 'opacity-50 cursor-not-allowed'
                  )}
                  title={`Отметить прочитанными (${channelUnread})`}
                  data-testid="btn-mark-as-read"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Прочитано</span>
                  <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-white/20">
                    {channelUnread}
                  </span>
                </button>
              );
            }
            return null;
          })()}
        {/* AI кнопки */}
        <div className="self-start flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => setIsContextModalOpen(true)}
            className="p-2 rounded-xl hover:bg-indigo-50 text-slate-400 hover:text-indigo-600 transition-colors"
            title="Контекст пациента (CCS)"
          >
            <Brain className="w-5 h-5" />
          </button>
          <button
            onClick={() => setIsHistoryOpen(true)}
            className="p-2 rounded-xl hover:bg-violet-50 text-slate-400 hover:text-violet-600 transition-colors"
            title="История AI-черновиков"
          >
            <History className="w-5 h-5" />
          </button>
          <button
            onClick={() => setIsCopilotOpen(true)}
            className="p-2 rounded-xl hover:bg-violet-50 text-slate-400 hover:text-violet-600 transition-colors"
            title="AI Copilot — сгенерировать ответ"
          >
            <Sparkles className="w-5 h-5" />
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => setNotesOpen((open) => !open)}
              className="p-2 rounded-xl hover:bg-amber-50 text-slate-400 hover:text-amber-700 transition-colors"
              title="Служебные пометки"
              data-testid="btn-training-notes"
            >
              <StickyNote className="w-5 h-5" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setInspectorOpen((open) => !open)}
            className="rounded-lg p-2 text-shell-muted hover:bg-shell-hover hover:text-shell-text"
            title="Контекст пациента"
          >
            <PanelRight className="h-[18px] w-[18px]" />
          </button>
        </div>
      </div>

      {/* Привязка показывается только у выбранного канала, если отправка из-за неё невозможна. */}
      {(isWabaActive || ((activeChannel === 'telegram_bot' || activeChannel === 'telegram_client') && !(telegramBinding || telegramClientBinding)) || ((activeChannel === 'max' || activeChannel === 'max_client') && !maxBinding)) && (
      <div className="space-y-2 border-b border-shell-border px-3 py-2">
        {isWabaActive && (
          <WindowStatusIndicator
            enabled={!!tenantId && isPhoneNumber}
            isLoading={wabaWindowQuery.isLoading}
            data={wabaWindowQuery.data || null}
          />
        )}
        {(activeChannel === 'telegram_bot' || activeChannel === 'telegram_client') && !(telegramBinding || telegramClientBinding) && (isTelegramId || isPhoneNumber) && (
          <TelegramLinkPanel
            phoneNumber={phone}
            isLinked={false}
            telegramUsername={telegramBinding?.username || telegramClientBinding?.username}
            availableChannels={availableChannels}
            tenantHasTgClient={messengerStatus.telegram_client}
          />
        )}
        {(activeChannel === 'max' || activeChannel === 'max_client') && !maxBinding && (isMaxId || isPhoneNumber) && (
          <MaxLinkPanel
            phoneNumber={phone}
            isLinked={false}
            maxUsername={maxBinding?.username}
            availableChannels={availableChannels}
          />
        )}
      </div>
      )}
      
      {/* Messages - независимый скролл, изолирован от внешнего */}
      <div 
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain p-4" 
        style={chatBackgroundStyle}
      >
        {(hasMoreMessages || isFetchingMoreMessages) && (
          <div className="flex justify-center">
            <div className="px-3 py-1.5 rounded-full bg-shell-surface/90 text-xs text-shell-muted shadow-sm border border-shell-border">
              {isFetchingMoreMessages ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner size="sm" />
                  Загрузка ранних сообщений...
                </span>
              ) : (
                <span>Показаны последние {allMessages.length} из {totalMessages}. Прокрутите вверх для ранних сообщений.</span>
              )}
            </div>
          </div>
        )}
        {filteredMessages.length === 0 ? (
          <div className="text-center text-shell-muted py-8">
            Нет сообщений
          </div>
        ) : (
          // Дата: 27.01.2026 - showChannel=true всегда для badge канала
          // Дата: 05.02.2026 - tenantId для прокси медиа
          filteredMessages.map((msg, index) => {
            const previous = filteredMessages[index - 1];
            const sameGroup = Boolean(
              previous &&
              previous.direction === msg.direction &&
              previous.messenger === msg.messenger &&
              previous.messenger_type === msg.messenger_type,
            );
            return (
            <MessageBubble
              key={msg.id}
              message={msg}
              showChannel={!sameGroup}
              channel={msg.messenger}
              tenantId={tenantId}
              isSuperAdmin={isSuperAdmin}
              hasTrainingNote={notesByMessageId.has(msg.id)}
              onAddTrainingNote={() => {
                setSelectedNoteMessageId(msg.id);
                setNotesOpen(true);
              }}
            />
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input — скрыт во вкладке "Все" (только просмотр), отправка через конкретные каналы */}
      {/* Дата: 28.01.2026 - FIX: Вкладка "Все" — только просмотр, отправка через конкретные каналы */}
      {activeChannel === 'all' ? (
        <div className="min-h-[72px] border-t border-shell-border bg-shell-surface px-3 py-2">
          <div className="mb-2 flex items-center justify-between gap-3">
            <p className="text-xs leading-4 text-shell-muted">Выберите канал. Отправка выключена, канал сам не переключается.</p>
            <button type="button" className="shell-button-secondary h-9 shrink-0" onClick={() => setChannelMenuOpen(true)}>Выбрать канал</button>
          </div>
          <textarea disabled rows={1} className="shell-input w-full resize-none" placeholder="Написать..." data-testid="input-message" />
        </div>
      ) : (
        <div className="border-t border-shell-border p-4 relative bg-shell-surface">
          {/* Дата: 15.01.2026 - TODO-027a: popup выбора WABA шаблона по `/` */}
          <WABATemplatePicker
            isOpen={isWabaPickerOpen && isWabaActive}
            templates={wabaTemplates}
            query={wabaQuery}
            onQueryChange={setWabaQuery}
            onSelect={handlePickWabaTemplate}
            onClose={() => {
              setIsWabaPickerOpen(false);
              setWabaQuery('');
            }}
          />
          <WABATemplateForm
            isOpen={isWabaFormOpen}
            tenantId={tenantId}
            phone={phone}
            template={selectedWabaTemplate}
            patientName={patientName || patient?.full_name}
            onClose={() => setIsWabaFormOpen(false)}
            onSent={() => {
              setIsWabaFormOpen(false);
              setSelectedWabaTemplate(null);
              queryClient.invalidateQueries({ queryKey: ['messages'] });
              queryClient.invalidateQueries({ queryKey: ['conversations'] });
            }}
          />
          
          {/* Превью прикреплённого файла */}
          {attachedFile && (
            <div className="mb-3 p-3 bg-shell-elevated rounded-2xl border border-shell-border flex items-center gap-3">
              {attachedPreview ? (
                <img 
                  src={attachedPreview} 
                  alt="Превью" 
                  className="w-16 h-16 rounded-xl object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-shell-primary-soft flex items-center justify-center">
                  <FileText className="w-8 h-8 text-shell-primary" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-shell-text truncate">{attachedFile.name}</p>
                <p className="text-xs text-shell-muted">
                  {(attachedFile.size / 1024).toFixed(1)} KB • {attachedFile.type || 'Файл'}
                </p>
              </div>
              <button
                onClick={() => {
                  setAttachedFile(null);
                  setAttachedPreview(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="p-2 rounded-xl hover:bg-shell-hover transition-colors"
                title="Удалить файл"
              >
                <X className="w-4 h-4 text-shell-muted" />
              </button>
            </div>
          )}
          
          <ComposerAIBadge draft={aiDraft} onClear={() => setAiDraft(EMPTY_AI_DRAFT)} />

          <div className="flex items-end gap-2">
            {/* 05.02.2026 - Кнопка прикрепления файла */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setAttachedFile(file);
                  // Создаём превью для изображений
                  if (file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    reader.onload = (ev) => setAttachedPreview(ev.target?.result as string);
                    reader.readAsDataURL(file);
                  } else {
                    setAttachedPreview(null);
                  }
                }
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                'p-2.5 rounded-xl transition-all duration-200',
                'hover:bg-shell-hover text-shell-muted hover:text-shell-text',
                attachedFile && 'bg-shell-primary-soft text-shell-primary'
              )}
              title="Прикрепить файл"
              data-testid="btn-attach-file"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            
            <textarea
              ref={textareaRef}
              value={message}
              rows={1}
              onChange={(e) => {
                const val = e.target.value;
                setMessage(val);
                draftsRef.current[draftKey] = val;
                // Трекинг AI-черновика
                if (aiDraft.kind !== 'none') {
                  if (val === '') {
                    setAiDraft(EMPTY_AI_DRAFT);
                  } else if (val !== aiDraft.originalText && aiDraft.kind === 'ai_clean') {
                    setAiDraft(prev => ({ ...prev, kind: 'ai_edited' }));
                  }
                }
                // Auto-resize
                const el = e.target;
                el.style.height = 'auto';
                el.style.height = `${el.scrollHeight}px`;
                if (isWabaActive && val.startsWith('/')) {
                  setIsWabaPickerOpen(true);
                  setWabaQuery(val.slice(1));
                } else {
                  setIsWabaPickerOpen(false);
                  setWabaQuery('');
                }
              }}
              onKeyDown={(e) => {
                // TODO-027a: если WABA окно закрыто — разрешаем начинать только с "/"
                if (isWabaWindowClosed && !message.startsWith('/')) {
                  const allowed = new Set([
                    '/',
                    'Backspace',
                    'Delete',
                    'ArrowLeft',
                    'ArrowRight',
                    'ArrowUp',
                    'ArrowDown',
                    'Tab',
                    'Home',
                    'End',
                    'Escape',
                    'Enter',
                  ]);
                  if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key.length === 1 && !allowed.has(e.key)) {
                    e.preventDefault();
                    return;
                  }
                }

                if (e.key === 'Enter' && !e.shiftKey && enterSends && !e.ctrlKey && !e.metaKey) {
                  e.preventDefault();
                  handleSend();
                  return;
                }
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={attachedFile ? 'Добавьте подпись к файлу...' : (isWabaWindowClosed && !message.startsWith('/') ? 'Окно WABA закрыто — нажмите /' : (enterSends ? 'Написать... Enter отправляет, Shift+Enter — новая строка' : 'Написать... Enter — новая строка, Ctrl+Enter — отправить'))}
              className="shell-input flex-1 resize-none overflow-hidden leading-normal"
              style={{ maxHeight: '200px', overflowY: 'auto' }}
              data-testid="input-message"
            />
            <Button 
              onClick={handleSend}
              isLoading={isSending}
              disabled={(!message.trim() && !attachedFile) || (isWabaWindowClosed && !message.startsWith('/') && !attachedFile)}
              testId="btn-send-message"
              className={aiDraft.kind !== 'none' ? 'bg-violet-600 hover:bg-violet-700' : undefined}
            >
              {aiDraft.kind !== 'none'
                ? <span className="flex items-center gap-1.5 text-xs font-semibold"><Sparkles className="w-3.5 h-3.5" />Подтвердить</span>
                : <Send className="w-4 h-4" />
              }
            </Button>
          </div>
          <label className="mt-2 flex items-center gap-2 text-[11px] text-shell-muted">
            <input
              type="checkbox"
              checked={enterSends}
              onChange={(event) => {
                setEnterSends(event.target.checked);
                window.localStorage.setItem('mt.composer.enterSends', event.target.checked ? '1' : '0');
              }}
            />
            Enter отправляет сообщение
          </label>
        </div>
      )}
      {/* Модалка выбора канала для request-contact (TG Bot / TG Client) */}
      {isContactChannelModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-shell-strong/60 backdrop-blur-sm">
          <div className="shell-card p-6 w-full max-w-sm mx-4 animate-in fade-in zoom-in-95 duration-200">
            <h3 className="text-lg font-semibold text-shell-strong mb-1">Запросить контакт</h3>
            <p className="text-sm text-shell-muted mb-5">Выберите канал для отправки запроса контакта пациенту</p>
            
            <div className="space-y-3">
              {/* TG Bot — нативная кнопка «Поделиться контактом» */}
              <button
                disabled={requestContactMutation.isPending}
                onClick={() => {
                  const mid = getMessengerIDFromPhone();
                  if (mid) sendRequestContact(mid, 'telegram_bot');
                }}
                className={cn(
                  'w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all duration-150',
                  'border-blue-200 hover:border-blue-400 hover:bg-blue-50',
                  requestContactMutation.isPending && 'opacity-50 cursor-not-allowed'
                )}
              >
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xl">🤖</span>
                </div>
                <div className="text-left">
                  <div className="font-medium text-shell-text">Через TG Bot</div>
                  <div className="text-xs text-shell-muted">Нативная кнопка «Поделиться контактом» прямо в боте</div>
                </div>
              </button>
              
              {/* TG Client — deep link на бота через сообщение в личку */}
              <button
                disabled={requestContactMutation.isPending}
                onClick={() => {
                  const mid = getMessengerIDFromPhone();
                  if (mid) sendRequestContact(mid, 'telegram_client');
                }}
                className={cn(
                  'w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all duration-150',
                  'border-purple-200 hover:border-purple-400 hover:bg-purple-50',
                  requestContactMutation.isPending && 'opacity-50 cursor-not-allowed'
                )}
              >
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xl">💬</span>
                </div>
                <div className="text-left">
                  <div className="font-medium text-shell-text">Через TG Client</div>
                  <div className="text-xs text-shell-muted">Пациент получит ссылку на бота для подтверждения номера</div>
                </div>
              </button>
            </div>
            
            {requestContactMutation.isPending && (
              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-shell-muted">
                <Spinner size="sm" />
                <span>Отправка запроса...</span>
              </div>
            )}
            
            <button
              onClick={() => setIsContactChannelModalOpen(false)}
              disabled={requestContactMutation.isPending}
              className="mt-4 w-full py-2 text-sm text-shell-muted hover:text-shell-text transition-colors"
            >
              Отмена
            </button>
          </div>
        </div>
      )}
      {/* Patient Context Modal - 04.05.2026 */}
      <PatientContextModal
        phone={phone}
        tenantId={tenantId || ''}
        isOpen={isContextModalOpen}
        onClose={() => setIsContextModalOpen(false)}
        patientName={patientName}
      />

      {/* AI Copilot Drawer */}
      <CopilotDrawer
        isOpen={isCopilotOpen}
        onClose={() => setIsCopilotOpen(false)}
        phone={phone}
        tenantId={tenantId || ''}
        onApplyVariant={handleApplyVariant}
      />

      {/* AI Copilot History Modal */}
      <CopilotHistoryModal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        tenantId={tenantId || ''}
        patientId={''}
      />
    </div>
    {inspectorOpen && (
      <aside className="flex w-[300px] min-w-0 shrink-0 flex-col overflow-y-auto border-l border-shell-border bg-shell-surface">
        <div className="flex items-center justify-between border-b border-shell-border px-3 py-3">
          <p className="text-sm font-semibold text-shell-strong">Пациент</p>
          <button type="button" className="text-xs text-shell-muted" onClick={() => setInspectorOpen(false)}>Закрыть</button>
        </div>
        <div className="space-y-4 px-3 py-3 text-sm">
          <div>
            <p className="font-semibold text-shell-strong">{patientName || patient?.full_name || 'Без имени'}</p>
            <p className="text-shell-muted">{phone}</p>
          </div>
          <div>
            <p className="mb-1 text-xs font-semibold text-shell-muted">Каналы для отправки</p>
            <p className="text-shell-text">{availableChannels.length ? availableChannels.join(', ') : 'Нет доступных каналов'}</p>
          </div>
          <div>
            <p className="mb-1 text-xs font-semibold text-shell-muted">Связь</p>
            <p className="text-shell-text">Telegram: {(telegramBinding || telegramClientBinding) ? 'связан' : 'не связан'}</p>
            <p className="text-shell-text">MAX: {maxBinding ? 'связан' : 'не связан'}</p>
          </div>
          {family && (
            <Link to={`/families/${family.id}`} className="text-shell-primary-strong">Семья: {family.name}</Link>
          )}
          {patient?.id && (
            <Link to={`/patients`} className="block text-shell-primary-strong">Открыть список пациентов</Link>
          )}
          {isSuperAdmin && (
            <details className="text-xs text-shell-muted">
              <summary className="cursor-pointer">Техническая диагностика</summary>
              <p className="mt-1 break-all">phone: {phone}</p>
              <p className="break-all">tenant: {tenantId || '—'}</p>
            </details>
          )}
        </div>
      </aside>
    )}
    {isSuperAdmin && notesOpen && tenantId && (
      <TrainingNotesPanel
        tenantId={tenantId}
        conversationId={phone}
        selectedMessageId={selectedNoteMessageId}
        notes={notesQuery.data || []}
        onClose={() => {
          setNotesOpen(false);
          setSelectedNoteMessageId(null);
        }}
        onChanged={() => {
          void notesQuery.refetch();
        }}
      />
    )}
    </div>
  );
}

